import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useCopyToClipboard } from 'lib/Hooks';

import './styles.themed.scss';

/**
 * A button component that copies text to the clipboard.
 * @param {object} props - The properties object.
 * @param {string} props.buttonText - The text displayed on the button.
 * @param {string} props.copiedText - The text to be copied to the clipboard.
 * @param {string} [props.buttonClasses] - Additional CSS classes for the button.
 * @param {string} [props.tooltipText] - Custom tooltip text displayed after copying.
 * @param {string} [props.tooltipClasses] - Additional CSS classes for the tooltip.
 * @param {Function} [props.handleClick] - Optional callback function executed on button click.
 */
const CopyToClipboardButton = ({
  buttonText,
  copiedText,
  buttonClasses = '',
  tooltipText = '',
  tooltipClasses = '',
  handleClick,
}) => {
  const { t } = useTranslation();

  const [isCopied, handleCopyToClipboard] = useCopyToClipboard();

  const buttonClassString = classNames(
    { copied: isCopied },
    'copy-to-clipboard__button',
    buttonClasses,
  );

  const tooltipClassString = classNames(
    'copy-to-clipboard__tooltip',
    'arrow-top',
    tooltipClasses,
  );

  /**
   * Handles the click event for copying text to the clipboard.
   * Calls the `handleCopyToClipboard` function with the provided `copiedText`.
   * If a `handleClick` callback is provided, it will also be executed.
   */
  const handleCopyClick = () => {
    handleCopyToClipboard(copiedText);

    if (handleClick) {
      handleClick();
    }
  };

  return (
    <div className="copy-to-clipboard">
      <button
        data-testid="copy-to-clipboard-button"
        type="button"
        className={buttonClassString}
        onClick={handleCopyClick}
      >
        {buttonText}
      </button>
      <span className={tooltipClassString}>
        {tooltipText || t('Copied')}
      </span>
    </div>
  );
};

CopyToClipboardButton.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonClasses: PropTypes.string,
  copiedText: PropTypes.string.isRequired,
  tooltipText: PropTypes.string,
  tooltipClasses: PropTypes.string,
  handleClick: PropTypes.func,
};


export { CopyToClipboardButton };
